import { useEffect, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { store } from 'app/store';
import {
  JobFulfillmentPaper,
  PaperChooseTypeEnum,
  SearchScopeEnum,
  UpdateJobSpecParam,
} from 'domains/jobconfigDomain';
import { useUser } from 'hooks/useUser';
import { PAPER_COLOR_WHITE } from 'utils/constants';
import { getSelectedSpecItem, StepTypeEnum } from 'utils/jobConfig';

/**
 * Proactively fetches MY_PAPER options and auto-selects the first filtered
 * paper immediately after Step1Quantity is completed — rather than waiting
 * until Step5PartPaper is opened.  This ensures the paper is selected even
 * when the user adds to cart directly from Step1 without navigating through
 * Step5.
 *
 * Conditions:
 *  - org.defaultChooseMyPaper must be true
 *  - The job must be in its initial state (spec.init === true)
 *  - Runs once per PART_PAPER part after step moves past Step1Quantity
 */
export const useMyPaperAutoSelect = () => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const step = useAppSelector((s) => s.jobConfig.step);
  const stepInfos = useAppSelector((s) => s.jobConfig.stepInfos);
  const configItem = useAppSelector((s) => s.jobConfig.configItem);
  const orgList = useAppSelector((s) => s.org.list);
  const sku = useAppSelector((s) => s.jobConfig.sku);

  const curOrg = useMemo(() => {
    const curOrgId = localStorage.getItem('org') || user?.orgId;
    return orgList.find((org) => org.id === Number(curOrgId));
  }, [orgList, user]);

  // Track which partSkuIds have already been auto-selected in this session
  const autoSelectedRef = useRef<{ [partSkuId: number]: boolean }>({});

  const quantitiesStepIdx = useMemo(
    () => stepInfos.findIndex((s) => s.type === StepTypeEnum.QUANTITIES),
    [stepInfos]
  );

  useEffect(() => {
    if (quantitiesStepIdx < 0) return;

    // Reset tracking only if user navigates to a step before Step1Quantity
    if (step < quantitiesStepIdx) {
      autoSelectedRef.current = {};
      return;
    }

    // Trigger from Step1Quantity onward — Job Config already exists at this point
    if (!curOrg?.defaultChooseMyPaper) return;

    // Only auto-select for fresh/initial jobs
    const specInit = getSelectedSpecItem(configItem)?.spec?.init;
    if (!specInit) return;

    const partPaperStepInfos = stepInfos.filter(
      (s) => s.type === StepTypeEnum.PART_PAPER && s.partSpec?.skuId
    );
    if (!partPaperStepInfos.length) return;

    partPaperStepInfos.forEach(async (stepInfo) => {
      const partSpec = stepInfo.partSpec!;
      if (autoSelectedRef.current[partSpec.skuId]) return;
      autoSelectedRef.current[partSpec.skuId] = true;

      try {
        // Fetch MY_PAPER options (cached in Redux if already loaded)
        await dispatch.jobConfig.getOptionPaper({
          jobConfigId: configItem.id,
          searchScope: SearchScopeEnum.MY_PAPER,
          targetSkuId: partSpec.skuId,
        });

        // Read updated state directly from store after the async dispatch
        const state = store.getState();
        const myPaperList = state.jobConfig.optionPaper[
          SearchScopeEnum.MY_PAPER
        ]?.find(
          (item) =>
            item.jobConfigId === configItem.id &&
            item.targetSkuId === partSpec.skuId
        )?.list;

        if (!myPaperList?.length) return;

        // Apply the same filters used in Step5PartPaper's selectOptions
        const filteredList = myPaperList
          .filter((item) => {
            if (
              partSpec.coatingBack !== 'none' &&
              partSpec.coatingFront !== 'none'
            ) {
              return (
                !item.category.includes('uncoated') &&
                !item.category.includes('c1s_coated_cover')
              );
            } else if (partSpec.coatingFront !== 'none') {
              return !item.category.includes('uncoated');
            }
            return true;
          })
          .filter((item) => {
            if (curOrg?.defaultSearchWhitePaper) {
              return item.color === PAPER_COLOR_WHITE;
            }
            return true;
          });

        if (!filteredList.length) return;

        const firstItem = filteredList[0];
        const paperDetails: Partial<Omit<JobFulfillmentPaper, 'paperSearchScope'>> = {
          paperName: firstItem.brand,
          paperBasisWeight: firstItem.basisWeight,
          paperCategory: firstItem.category,
          paperColor: firstItem.color,
          paperFinish: firstItem.finish,
          paperLengthIn: firstItem.lengthIn,
          paperWidthIn: firstItem.widthIn,
          paperConstraint1: firstItem.constraint1 || undefined,
          paperConstraint2: firstItem.constraint2 || undefined,
        };

        const payload: UpdateJobSpecParam = {
          skuId: sku.id,
          parts: [
            {
              skuId: partSpec.skuId,
              paperChooseType: PaperChooseTypeEnum.SEARCH,
              paperBrand: firstItem.brand,
              ...paperDetails,
              paperSearchScope: SearchScopeEnum.MY_PAPER,
              constraint1Options: firstItem.constraint1
                ? [firstItem.constraint1]
                : undefined,
              constraint2Options: firstItem.constraint2
                ? [firstItem.constraint2]
                : undefined,
            },
          ],
        };

        await dispatch.jobConfig.updateJobSpec(payload);
        dispatch.jobConfig.resetErrorStep();
      } catch {
        // Silent fail — Step5PartPaper will retry when the user navigates there
        autoSelectedRef.current[partSpec.skuId] = false;
      }
    });
  }, [step, quantitiesStepIdx, curOrg, configItem, stepInfos, sku, dispatch.jobConfig]);
};
